import type { Vec2 } from '../../shared/vec';

export interface Segment {
  readonly from: Vec2;
  readonly to: Vec2;
}

export interface Span {
  readonly control: Vec2;
  readonly to: Vec2;
}

export interface Triangle {
  readonly at: Vec2;
  readonly angle: number;
  readonly cream: boolean;
}

export interface Lantern {
  readonly center: Vec2;
  readonly stars: readonly Vec2[];
}

export interface TentLayout {
  readonly anchors: readonly Vec2[];
  readonly scallops: readonly Span[];
  readonly seams: readonly Segment[];
  readonly ropes: readonly Segment[];
  readonly triangles: readonly Triangle[];
  readonly lanterns: readonly Lantern[];
  readonly poufs: readonly Vec2[];
}

export const TENT_REACH = { x: 520, y: 400 };
export const ROPE_LENGTH = 50;
export const PEG = 8;
export const EDGE_WIDTH = 20;
const TRIANGLES_PER_SPAN = 9;
export const LANTERN_SIZE = 22;
export const POUF_RADIUS = 15;

const ANCHORS = 14;
const ANCHOR_RADII = [500, 470] as const;
const ANCHOR_TURN = 0.12;
// Squeezed less in height, so that the north mast stays well under the cloth.
const ANCHOR_STRETCH = { x: 1.22 * 0.72, y: 0.74 * 0.9 };
const SCALLOP = 0.86;
const ROPE_SPLAY = 0.07;
const LANTERN_RADIUS = 190;
const LANTERN_STRETCH = { x: 1.3, y: 0.95 };
const LANTERN_STAR_RADIUS = LANTERN_SIZE * 3.1;
const POUFS: readonly Vec2[] = [
  { x: 377, y: 407 },
  { x: -376, y: 369 },
  { x: -697, y: -76 },
  { x: -498, y: -298 },
  { x: -188, y: -460 },
  { x: 210, y: -437 },
  { x: 530, y: -290 },
  { x: 718, y: -58 },
];

const toward = (center: Vec2, radius: number, angle: number, stretch = { x: 1, y: 1 }): Vec2 => ({
  x: center.x + radius * Math.cos(angle) * stretch.x,
  y: center.y + radius * Math.sin(angle) * stretch.y,
});

function quadraticAt(from: Vec2, { control, to }: Span, t: number): Vec2 {
  const u = 1 - t;
  return {
    x: u * u * from.x + 2 * u * t * control.x + t * t * to.x,
    y: u * u * from.y + 2 * u * t * control.y + t * t * to.y,
  };
}

export function tentLayout(center: Vec2, masts: readonly Vec2[]): TentLayout {
  const anchors = Array.from({ length: ANCHORS }, (_, index) =>
    toward(
      center,
      ANCHOR_RADII[index % 2] ?? 0,
      (index / ANCHORS) * Math.PI * 2 + ANCHOR_TURN,
      ANCHOR_STRETCH,
    ),
  );

  const scallops = anchors.map((from, index) => {
    const to = anchors[(index + 1) % anchors.length] ?? from;
    return {
      control: {
        x: center.x + ((from.x + to.x) / 2 - center.x) * SCALLOP,
        y: center.y + ((from.y + to.y) / 2 - center.y) * SCALLOP,
      },
      to,
    };
  });

  const seams = anchors.flatMap((from) =>
    [...masts]
      .sort(
        (a, b) => Math.hypot(a.x - from.x, a.y - from.y) - Math.hypot(b.x - from.x, b.y - from.y),
      )
      .slice(0, 2)
      .map(({ x, y }) => ({ from, to: { x, y } })),
  );

  const ropes = anchors.flatMap((from) => {
    const out = Math.atan2(from.y - center.y, from.x - center.x);
    return [out - ROPE_SPLAY, out + ROPE_SPLAY].map((angle) => ({
      from,
      to: { x: from.x + ROPE_LENGTH * Math.cos(angle), y: from.y + ROPE_LENGTH * Math.sin(angle) },
    }));
  });

  const triangles = scallops.flatMap((span, index) => {
    const from = anchors[index] ?? span.to;
    return Array.from({ length: TRIANGLES_PER_SPAN }, (_, step) => {
      const t = (step + 0.5) / TRIANGLES_PER_SPAN;
      const u = 1 - t;
      const tangent = {
        x: u * (span.control.x - from.x) + t * (span.to.x - span.control.x),
        y: u * (span.control.y - from.y) + t * (span.to.y - span.control.y),
      };
      return {
        at: quadraticAt(from, span, t),
        angle: Math.atan2(tangent.y, tangent.x),
        cream: step % 2 === 1,
      };
    });
  });

  const lanterns = Array.from({ length: 5 }, (_, index) => {
    const lantern = toward(
      center,
      LANTERN_RADIUS,
      ((-106 + index * 68) * Math.PI) / 180 - 0.3,
      LANTERN_STRETCH,
    );
    return {
      center: lantern,
      stars: Array.from({ length: 8 }, (_, star) =>
        toward(lantern, LANTERN_STAR_RADIUS, (star * Math.PI) / 4 + Math.PI / 8),
      ),
    };
  });

  const poufs = POUFS.map(({ x, y }) => ({ x: center.x + x, y: center.y + y }));

  return { anchors, scallops, seams, ropes, triangles, lanterns, poufs };
}

export interface CreatureLayout {
  readonly at: Vec2;
  readonly rotation: number;
  readonly scale: number;
  readonly wings: readonly (readonly Vec2[])[];
  readonly ribs: readonly Segment[];
  readonly rungs: readonly Segment[];
  readonly braces: readonly Segment[];
  readonly tail: readonly Vec2[];
  readonly tailRibs: readonly Segment[];
  readonly neck: readonly [Vec2, Vec2, Vec2, Vec2];
  readonly neckDashes: readonly Segment[];
  readonly body: { readonly rx: number; readonly ry: number };
  readonly bodyLines: readonly Segment[];
  readonly head: readonly Vec2[];
  readonly eyes: readonly Vec2[];
}

const NECK = 250;
const WING_BASE = { x: 10, y: 30 };
const WING_TIPS: readonly (readonly [number, number])[] = [
  [30, 70],
  [40, 190],
  [10, 310],
  [-30, 400],
  [-90, 370],
  [-80, 290],
  [-120, 250],
  [-100, 160],
  [-130, 110],
  [-70, 40],
];
const LATTICE = [0.35, 0.6, 0.82];
const BRACE = 0.12;
const NECK_STEPS = 128;
const NECK_DASH = { on: 7, off: 6 };

const between = (from: Vec2, to: Vec2, t: number): Vec2 => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
});

function bezier(points: readonly Vec2[], t: number): Vec2 {
  const [first, ...rest] = points;
  if (first === undefined || rest.length === 0) {
    return first ?? { x: 0, y: 0 };
  }
  return bezier(
    rest.map((to, index) => between(points[index] ?? to, to, t)),
    t,
  );
}

function dashed(curve: readonly Vec2[]): Segment[] {
  const points = Array.from({ length: NECK_STEPS + 1 }, (_, step) =>
    bezier(curve, step / NECK_STEPS),
  );
  let walked = 0;
  return points.slice(1).flatMap((to, index) => {
    const from = points[index] ?? to;
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    const middle = (walked + length / 2) % (NECK_DASH.on + NECK_DASH.off);
    walked += length;
    return middle < NECK_DASH.on ? [{ from, to }] : [];
  });
}

export function creatureLayout(center: Vec2): CreatureLayout {
  const sides = [1, -1].map((side) => ({
    base: { x: WING_BASE.x, y: WING_BASE.y * side },
    tips: WING_TIPS.map(([x, y]) => ({ x, y: y * side })),
  }));
  const lattice = sides.flatMap(({ base, tips }) =>
    LATTICE.flatMap((t) =>
      tips.slice(0, -1).map((tip, index) => {
        const next = tips[index + 1] ?? tip;
        return {
          rung: { from: between(base, tip, t), to: between(base, next, t) },
          brace: { from: between(base, tip, t), to: between(base, next, t + BRACE) },
        };
      }),
    ),
  );
  const head = 60 + NECK;
  const neck: [Vec2, Vec2, Vec2, Vec2] = [
    { x: 50, y: 0 },
    { x: 50 + NECK * 0.4, y: -30 },
    { x: 50 + NECK * 0.7, y: 26 },
    { x: head - 18, y: 0 },
  ];
  return {
    at: { x: center.x - 20, y: center.y + 405 },
    rotation: -Math.PI / 2,
    scale: 0.56,
    wings: sides.map(({ base, tips }) => [base, ...tips]),
    ribs: sides.flatMap(({ base, tips }) => tips.map((to) => ({ from: base, to }))),
    rungs: lattice.map(({ rung }) => rung),
    braces: lattice.map(({ brace }) => brace),
    tail: [
      { x: -60, y: -14 },
      { x: -190, y: -60 },
      { x: -210, y: 0 },
      { x: -190, y: 60 },
      { x: -60, y: 14 },
    ],
    tailRibs: [
      { x: -205, y: -30 },
      { x: -210, y: 0 },
      { x: -205, y: 30 },
    ].map((to) => ({ from: { x: -60, y: 0 }, to })),
    neck,
    neckDashes: dashed(neck),
    body: { rx: 78, ry: 42 },
    bodyLines: [
      { from: { x: -60, y: 0 }, to: { x: 60, y: 0 } },
      { from: { x: -40, y: -32 }, to: { x: 40, y: 32 } },
      { from: { x: -40, y: 32 }, to: { x: 40, y: -32 } },
    ],
    head: [
      { x: head - 20, y: -16 },
      { x: head + 22, y: -10 },
      { x: head + 54, y: 0 },
      { x: head + 22, y: 10 },
      { x: head - 20, y: 16 },
    ],
    eyes: [
      { x: head + 8, y: -5 },
      { x: head + 8, y: 5 },
    ],
  };
}
